import React, {lazy} from 'react'
import {RouteComponentProps} from 'react-router-dom'

import * as routes from 'shared/routes'
import * as typePowerPlant from 'shared/typePowerPlant'
import * as titles from 'constants/titles'
import {getFeaturePermissions, FlagPages} from 'config/featureFlags'
import {
  ADMINISTRATION_CREATE,
  ADMINISTRATION_EDIT,
  ADMINISTRATION_VIEW,
  BUSINESS_PLAN_ONLY_VIEW,
  BUSINESS_PLAN_AND_LOGS_VIEW,
  DASHBOARD_VIEW,
  FORECAST_VIEW,
  MAP_VIEW,
  OPERATION_JOURNAL_CREATE,
  OPERATION_JOURNAL_COMMENT_CREATE,
  OPERATION_JOURNAL_EDIT,
  OPERATION_JOURNAL_VIEW,
  POWER_BI_VIEW,
  RESTRICTIONS_JOURNAL_CREATE,
  RESTRICTIONS_JOURNAL_EDIT,
  RESTRICTIONS_JOURNAL_VIEW,
  UNBALANCE_VIEW,
  OPERATION_JOURNAL_SHIFT_ACCEPTANCE_VIEW,
  OPERATION_JOURNAL_SHIFT_TRANSFER_VIEW,
  OPERATION_JOURNAL_SHIFTS_HISTORY_VIEW,
} from 'constants/permissions'

const Dashboard = lazy(() => import('pages/Dashboard'))

const Charts = lazy(() => import('pages/Dashboard/Charts'))

const Users = lazy(() => import('pages/Users'))

const BusinessPlan = lazy(() => import('pages/BusinessPlan'))
const BusinessPlanDetails = lazy(() => import('pages/BusinessPlan/Details'))

const PowerBI = lazy(() => import('pages/PowerBI'))

const Unbalance = lazy(() => import('pages/Unbalance'))
const UnbalanceCharts = lazy(() => import('pages/Unbalance/Charts'))

const MapPowerPlants = lazy(() => import('pages/MapPowerPlants'))

const GeneralStatistic = lazy(() => import('pages/Dashboard/GeneralStatistic'))

const Forecast = lazy(() => import('pages/Forecast'))
const ForecastStations = lazy(() => import('pages/Forecast/Stations'))

const RestrictionsJournal = lazy(() => import('pages/RestrictionsJournal'))
const RestrictionsJournalCreateEdit = lazy(() => import('pages/RestrictionsJournal/CreateEdit'))
const RestrictionsJournalView = lazy(() => import('pages/RestrictionsJournal/View'))

const OperationalJournalList = lazy(() => import('pages/OperationalJournal/Lists'))
const OperationalJournalCreate = lazy(
  () => import('pages/OperationalJournal/Logs/Create/CreateRequest'),
)
const OperationalJournalView = lazy(() => import('pages/OperationalJournal/Logs/View/ViewRequest'))
const OperationalJournalEdit = lazy(() => import('pages/OperationalJournal/Logs/Edit/EditRequest'))

const ShiftTransfer = lazy(() => import('pages/OperationalJournal/ShiftTransfer'))
const ShiftAcceptance = lazy(() => import('pages/OperationalJournal/ShiftAcceptance'))
const ShiftTransferHistoryDetails = lazy(
  () => import('pages/OperationalJournal/ShiftTransferHistoryDetails'),
)

const Administration = lazy(() => import('pages/Administration'))
const AdministrationCreateEdit = lazy(() => import('pages/Administration/CreateEdit'))
const AdministrationView = lazy(() => import('pages/Administration/View'))

// Type for route component props
interface RouteProps extends RouteComponentProps {
  typePowerPlant?: string
  crumbs: BreadcrumbItem[]
}

export const overlayRoutesList = [
  {
    exact: true,
    name: titles.OPERATION_JOURNAL_SHIFT_TRANSFER,
    path: routes.OPERATION_JOURNAL_SHIFT_TRANSFER,
    component: (props: RouteProps) => <ShiftTransfer {...props} />,
    permission: OPERATION_JOURNAL_SHIFT_TRANSFER_VIEW,
  },
  {
    exact: true,
    name: titles.OPERATION_JOURNAL_SHIFT_ACCEPTANCE,
    path: routes.OPERATION_JOURNAL_SHIFT_ACCEPTANCE,
    component: (props: RouteProps) => <ShiftAcceptance {...props} />,
    permission: OPERATION_JOURNAL_SHIFT_ACCEPTANCE_VIEW,
  },
]

export const routeList = [
  // Users page - controlled by feature flag
  ...getFeaturePermissions(FlagPages.USERS_PAGE, [
    {
      component: (props: RouteProps) => <Users {...props} />,
      path: routes.USERS,
      name: 'Users',
      exact: true,
    },
  ]),
  {
    component: (props: RouteProps) => <Dashboard {...props} />,
    path: routes.DASHBOARD,
    name: titles.DASHBOARD,
    exact: true,
    permission: DASHBOARD_VIEW,
  },
  {
    component: (props: RouteProps) => <Charts {...props} typePowerPlant={typePowerPlant.SPP} />,
    path: routes.CHARTS_SPP,
    name: titles.SPP,
    exact: true,
    permission: DASHBOARD_VIEW,
  },
  {
    component: (props: RouteProps) => <Charts {...props} typePowerPlant={typePowerPlant.WPP} />,
    path: routes.CHARTS_WPP,
    name: titles.WPP,
    exact: true,
    permission: DASHBOARD_VIEW,
  },
  {
    component: (props: RouteProps) => <GeneralStatistic {...props} />,
    path: routes.CHARTS_GENERAL_STATISTIC,
    name: titles.GENERAL_STATISTIC,
    exact: true,
    permission: DASHBOARD_VIEW,
  },
  {
    component: (props: RouteProps) => <PowerBI {...props} />,
    path: routes.POWER_BI,
    name: titles.POWER_BI,
    exact: true,
    permission: POWER_BI_VIEW,
  },

  // Unbalance pages - controlled by feature flag
  ...getFeaturePermissions(FlagPages.UNBALANCE_PAGE, [
    {
      component: (props: RouteProps) => <Unbalance {...props} />,
      path: routes.UNBALANCE,
      name: titles.UNBALANCE,
      exact: true,
      permission: UNBALANCE_VIEW,
    },
    {
      component: (props: RouteProps) => (
        <UnbalanceCharts {...props} typePowerPlant={typePowerPlant.SPP} />
      ),
      path: routes.UNBALANCE_SPP,
      name: titles.SPP,
      exact: true,
      permission: UNBALANCE_VIEW,
    },
    {
      component: (props: RouteProps) => (
        <UnbalanceCharts {...props} typePowerPlant={typePowerPlant.WPP} />
      ),
      path: routes.UNBALANCE_WPP,
      name: titles.WPP,
      exact: true,
      permission: UNBALANCE_VIEW,
    },
  ]),

  {
    component: (props: RouteProps) => <Forecast {...props} />,
    path: routes.FORECAST,
    name: titles.FORECAST,
    exact: true,
    permission: FORECAST_VIEW,
  },
  {
    component: (props: RouteProps) => (
      <ForecastStations {...props} typePowerPlant={typePowerPlant.SPP} />
    ),
    path: routes.FORECAST_SPP,
    name: titles.SPP,
    exact: true,
    permission: FORECAST_VIEW,
  },
  {
    component: (props: RouteProps) => (
      <ForecastStations {...props} typePowerPlant={typePowerPlant.WPP} />
    ),
    path: routes.FORECAST_WPP,
    name: titles.WPP,
    exact: true,
    permission: FORECAST_VIEW,
  },

  // MapPowerPlants page - controlled by feature flag
  ...getFeaturePermissions(FlagPages.MAP_PAGE, [
    {
      component: (props: RouteProps) => <MapPowerPlants {...props} />,
      path: routes.MAP,
      name: titles.MAP,
      exact: true,
      permission: MAP_VIEW,
    },
  ]),

  {
    component: (props: RouteProps) => <BusinessPlan {...props} />,
    path: routes.BUSINESS_PLAN,
    name: titles.BUSINESS_PLAN,
    exact: true,
    permission: [BUSINESS_PLAN_ONLY_VIEW, BUSINESS_PLAN_AND_LOGS_VIEW],
  },
  {
    component: (props: RouteProps) => <BusinessPlanDetails {...props} />,
    path: routes.BUSINESS_PLAN_DETAILS,
    name: titles.WPP_SPP,
    exact: true,
    permission: [BUSINESS_PLAN_ONLY_VIEW, BUSINESS_PLAN_AND_LOGS_VIEW],
  },

  // Administration pages - controlled by feature flag
  ...getFeaturePermissions(FlagPages.ADMINISTRATION_PAGE, [
    {
      component: (props: RouteProps) => <Administration {...props} />,
      path: routes.ADMINISTRATION,
      name: titles.ADMINISTRATION,
      exact: true,
      permission: ADMINISTRATION_VIEW,
    },
    {
      component: (props: RouteProps) => <AdministrationCreateEdit {...props} />,
      path: routes.ADMINISTRATION_CREATE,
      name: titles.ADMINISTRATION_CREATE,
      exact: true,
      permission: ADMINISTRATION_CREATE,
    },
    {
      component: (props: RouteProps) => <AdministrationCreateEdit {...props} />,
      path: routes.ADMINISTRATION_EDIT,
      name: titles.ADMINISTRATION_EDIT,
      exact: true,
      permission: ADMINISTRATION_EDIT,
    },
    {
      component: (props: RouteProps) => <AdministrationView {...props} />,
      path: routes.ADMINISTRATION_VIEW,
      name: titles.ADMINISTRATION_VIEW,
      exact: true,
      permission: ADMINISTRATION_VIEW,
    },
  ]),

  // Restrictions Journal pages - controlled by feature flag
  ...getFeaturePermissions(FlagPages.RESTRICTIONS_JOURNAL_PAGE, [
    {
      component: (props: RouteProps) => <RestrictionsJournal {...props} />,
      path: routes.RESTRICTIONS_JOURNAL,
      name: titles.RESTRICTIONS_JOURNAL,
      exact: true,
      permission: RESTRICTIONS_JOURNAL_VIEW,
    },
    {
      component: (props: RouteProps) => <RestrictionsJournalCreateEdit {...props} />,
      path: routes.RESTRICTIONS_JOURNAL_CREATE,
      name: titles.RESTRICTIONS_JOURNAL_CREATE,
      exact: true,
      permission: RESTRICTIONS_JOURNAL_CREATE,
    },
    {
      component: (props: RouteProps) => <RestrictionsJournalView {...props} />,
      path: routes.RESTRICTIONS_JOURNAL_VIEW_DETAILS,
      name: titles.RESTRICTIONS_JOURNAL_VIEW,
      exact: true,
      permission: RESTRICTIONS_JOURNAL_VIEW,
    },
    {
      component: (props: RouteProps) => <RestrictionsJournalCreateEdit {...props} />,
      path: routes.RESTRICTIONS_JOURNAL_VIEW_EDIT,
      name: titles.RESTRICTIONS_JOURNAL_EDIT,
      exact: true,
      permission: RESTRICTIONS_JOURNAL_EDIT,
    },
  ]),
  {
    component: (props: RouteProps) => <OperationalJournalList {...props} />,
    path: routes.OPERATION_JOURNAL,
    name: titles.OPERATION_JOURNAL,
    exact: true,
    permission: OPERATION_JOURNAL_VIEW,
  },
  {
    component: (props: RouteProps) => <OperationalJournalCreate {...props} />,
    path: routes.OPERATION_JOURNAL_CREATE,
    name: titles.OPERATION_JOURNAL_CREATE,
    exact: true,
    permission: [OPERATION_JOURNAL_CREATE, OPERATION_JOURNAL_COMMENT_CREATE],
  },
  {
    component: (props: RouteProps) => <OperationalJournalView {...props} />,
    path: routes.OPERATION_JOURNAL_VIEW,
    name: titles.OPERATION_JOURNAL_VIEW,
    exact: true,
    permission: OPERATION_JOURNAL_VIEW,
  },
  {
    component: (props: RouteProps) => <OperationalJournalEdit {...props} />,
    path: routes.OPERATION_JOURNAL_EDIT,
    name: titles.OPERATION_JOURNAL_EDIT,
    exact: true,
    permission: OPERATION_JOURNAL_EDIT,
  },
  {
    exact: true,
    component: (props: RouteProps) => <ShiftTransferHistoryDetails {...props} />,
    path: routes.OPERATION_JOURNAL_SHIFTS_HISTORY_DETAILS,
    name: titles.OPERATION_JOURNAL_SHIFTS_HISTORY_DETAILS,
    permission: OPERATION_JOURNAL_SHIFTS_HISTORY_VIEW,
  },
]
